import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import SafeChart from '../components/SafeChart';
import AthleteContextHeader from '../components/AthleteContextHeader';
import SearchableAthleteSelect from '../components/SearchableAthleteSelect';
import RatingSparkline from '../components/RatingSparkline';
import { PERMISSION_MODULES as PM } from '../constants/permissionModules';
import { shouldLimitForContractFixture } from '../lib/visibility';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MatchReportsPage = ({ athleteId: propAthleteId }) => {
  const { user, hasModuleAccess } = useAuth();
  const isScopedMode = !!propAthleteId;

  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState('');
  const [config, setConfig] = useState(null);
  const [reportsData, setReportsData] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [createDialog, setCreateDialog] = useState(false);
  const [evalDialog, setEvalDialog] = useState(false);
  const [reflectDialog, setReflectDialog] = useState(false);
  const [detailDialog, setDetailDialog] = useState(false);

  // Forms
  const [newReport, setNewReport] = useState({
    opponent: '', match_date: '', competition: 'League', minutes_played: '',
    position: '', availability_status: 'Fit',
    metrics: {
      technical: { pass_accuracy: '', dribbles_success: '', dribbles_attempts: '', first_touch: '' },
      tactical: { positioning: '', decision_making: '' },
      physical: { distance_covered_km: '', sprint_count: '', stamina: '' },
      mental: { focus: '', confidence: '' }
    }
  });
  const [newEval, setNewEval] = useState({ overall_rating: '', strengths: '', areas_to_improve: '', tactical_notes: '' });
  const [newReflection, setNewReflection] = useState({ self_rating: '', what_went_well: '', what_to_improve: '' });
  const [injuryStatus, setInjuryStatus] = useState(null);
  const [trendData, setTrendData] = useState([]);

  const canEdit = user?.role === 'Admin' || hasModuleAccess(PM.MATCH_REPORTS, 'edit');
  const isPlayer = user?.role === 'Player';
  const isParent = user?.role === 'Parent';
  const isLinkedViewer = isPlayer || isParent;

  useEffect(() => {
    if (propAthleteId) {
      // Scoped mode: fetch single athlete, auto-select
      Promise.all([
        axios.get(`${API}/athletes/${propAthleteId}`),
        axios.get(`${API}/match-reports-v2/config`)
      ]).then(([athleteRes, configRes]) => {
        setAthletes([athleteRes.data]);
        setConfig(configRes.data);
        setSelectedAthlete(athleteRes.data.id);
        setLoading(false);
      }).catch(() => setLoading(false));
    } else {
      fetchInitialData();
    }
  }, [propAthleteId]);

  // Auto-select linked athlete for Player / Parent roles
  useEffect(() => {
    if (isLinkedViewer && athletes.length > 0 && !selectedAthlete) {
      setSelectedAthlete(athletes[0].id);
    }
  }, [isLinkedViewer, athletes, selectedAthlete]);

  useEffect(() => {
    if (selectedAthlete) {
      // Reset stale data before loading new athlete
      setReportsData(null);
      setTrendData([]);
      setSelectedReport(null);
      setInjuryStatus(null);
      fetchAthleteReports(selectedAthlete);
    }
  }, [selectedAthlete]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [athletesRes, configRes] = await Promise.all([
        axios.get(`${API}/athletes`),
        axios.get(`${API}/match-reports-v2/config`)
      ]);
      const athleteList = Array.isArray(athletesRes.data) ? athletesRes.data : (athletesRes.data.athletes || []);
      setAthletes(athleteList);
      setConfig(configRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAthleteReports = useCallback(async (athleteId) => {
    try {
      const response = await axios.get(`${API}/match-reports-v2/reports/athlete/${athleteId}`);
      setReportsData(response.data);
      // Build trend data from reports
      const reps = (response.data.reports || []).slice().reverse();
      const trends = reps.map(r => {
        const m = r.metrics || {};
        return {
          date: r.match_date ? new Date(r.match_date).toLocaleDateString('en-GB', {day:'2-digit', month:'short'}) : '',
          opponent: r.opponent,
          rating: r.coach_evaluation?.overall_rating || null,
          pass_accuracy: m.technical?.pass_accuracy ?? null,
          stamina: m.physical?.stamina ?? null,
          decision_making: m.tactical?.decision_making ?? null,
        };
      });
      setTrendData(trends);
    } catch (error) {
      toast.error('Failed to load reports');
      setReportsData(null);
      setTrendData([]);
    }
  }, []);

  const checkInjuryBeforeCreate = async () => {
    if (!selectedAthlete) {
      toast.error('Select an athlete first');
      return;
    }
    try {
      const res = await axios.get(`${API}/injury-recovery/status/athlete/${selectedAthlete}`);
      setInjuryStatus(res.data);
      if (res.data.has_active_injury) {
        setNewReport(prev => ({ ...prev, availability_status: 'Injured' }));
      } else {
        setNewReport(prev => ({ ...prev, availability_status: 'Fit' }));
      }
    } catch {
      setInjuryStatus(null);
    }
    setCreateDialog(true);
  };

  const buildMetrics = () => {
    const m = newReport.metrics;
    const technical = {};
    if (m.technical.pass_accuracy) technical.pass_accuracy = parseInt(m.technical.pass_accuracy);
    if (m.technical.dribbles_success) technical.dribbles_success = parseInt(m.technical.dribbles_success);
    if (m.technical.dribbles_attempts) technical.dribbles_attempts = parseInt(m.technical.dribbles_attempts);
    if (m.technical.first_touch) technical.first_touch = parseInt(m.technical.first_touch);

    const tactical = {};
    if (m.tactical.positioning) tactical.positioning = parseInt(m.tactical.positioning);
    if (m.tactical.decision_making) tactical.decision_making = parseInt(m.tactical.decision_making);

    const physical = {};
    if (m.physical.distance_covered_km) physical.distance_covered_km = parseFloat(m.physical.distance_covered_km);
    if (m.physical.sprint_count) physical.sprint_count = parseInt(m.physical.sprint_count);
    if (m.physical.stamina) physical.stamina = parseInt(m.physical.stamina);

    const mental = {};
    if (m.mental.focus) mental.focus = parseInt(m.mental.focus);
    if (m.mental.confidence) mental.confidence = parseInt(m.mental.confidence);

    const hasAny = Object.keys(technical).length || Object.keys(tactical).length || Object.keys(physical).length || Object.keys(mental).length;
    if (!hasAny) return null;

    return {
      technical: Object.keys(technical).length ? technical : null,
      tactical: Object.keys(tactical).length ? tactical : null,
      physical: Object.keys(physical).length ? physical : null,
      mental: Object.keys(mental).length ? mental : null
    };
  };

  const handleCreateReport = async () => {
    if (!newReport.opponent || !newReport.match_date) {
      toast.error('Opponent and date are required');
      return;
    }
    try {
      const payload = {
        athlete_id: selectedAthlete,
        opponent: newReport.opponent,
        match_date: newReport.match_date,
        competition: newReport.competition,
        minutes_played: newReport.minutes_played ? parseInt(newReport.minutes_played) : null,
        position: newReport.position || null,
        availability_status: newReport.availability_status,
        metrics: buildMetrics()
      };
      await axios.post(`${API}/match-reports-v2/reports`, payload);
      toast.success('Match report created');
      setCreateDialog(false);
      resetReportForm();
      fetchAthleteReports(selectedAthlete);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create report');
    }
  };

  const resetReportForm = () => {
    setNewReport({
      opponent: '', match_date: '', competition: 'League', minutes_played: '',
      position: '', availability_status: 'Fit',
      metrics: {
        technical: { pass_accuracy: '', dribbles_success: '', dribbles_attempts: '', first_touch: '' },
        tactical: { positioning: '', decision_making: '' },
        physical: { distance_covered_km: '', sprint_count: '', stamina: '' },
        mental: { focus: '', confidence: '' }
      }
    });
    setInjuryStatus(null);
  };

  const openReportDetail = async (reportId) => {
    try {
      const res = await axios.get(`${API}/match-reports-v2/reports/${reportId}`);
      setSelectedReport(res.data);
      setDetailDialog(true);
    } catch {
      toast.error('Failed to load report details');
    }
  };

  const openEvalDialog = (report) => {
    setSelectedReport(report);
    setNewEval({ overall_rating: '', strengths: '', areas_to_improve: '', tactical_notes: '' });
    setEvalDialog(true);
  };

  const openReflectDialog = (report) => {
    setSelectedReport(report);
    setNewReflection({ self_rating: '', what_went_well: '', what_to_improve: '' });
    setReflectDialog(true);
  };

  const handleSubmitEvaluation = async () => {
    if (!newEval.overall_rating || newEval.overall_rating < 1 || newEval.overall_rating > 10) {
      toast.error('Rating must be 1-10');
      return;
    }
    try {
      await axios.post(`${API}/match-reports-v2/evaluations`, {
        match_report_id: selectedReport.report?.id || selectedReport.id,
        athlete_id: selectedAthlete,
        overall_rating: parseInt(newEval.overall_rating),
        strengths: newEval.strengths || null,
        areas_to_improve: newEval.areas_to_improve || null,
        tactical_notes: newEval.tactical_notes || null
      });
      toast.success('Evaluation submitted');
      setEvalDialog(false);
      fetchAthleteReports(selectedAthlete);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit evaluation');
    }
  };

  const handleSubmitReflection = async () => {
    if (!newReflection.self_rating || newReflection.self_rating < 1 || newReflection.self_rating > 10) {
      toast.error('Self rating must be 1-10');
      return;
    }
    try {
      await axios.post(`${API}/match-reports-v2/reflections`, {
        match_report_id: selectedReport.report?.id || selectedReport.id,
        athlete_id: selectedAthlete,
        self_rating: parseInt(newReflection.self_rating),
        what_went_well: newReflection.what_went_well || null,
        what_to_improve: newReflection.what_to_improve || null
      });
      toast.success('Reflection submitted');
      setReflectDialog(false);
      fetchAthleteReports(selectedAthlete);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit reflection');
    }
  };

  const getRatingColor = (rating) => {
    if (!rating) return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-500';
    if (rating >= 8) return 'bg-emerald-100 text-emerald-800';
    if (rating >= 6) return 'bg-blue-100 text-blue-800';
    if (rating >= 4) return 'bg-amber-100 text-amber-800';
    return 'bg-red-100 text-red-800';
  };

  const getAvailabilityBadge = (status) => {
    switch (status) {
      case 'Fit': return 'bg-emerald-100 text-emerald-800';
      case 'Injured': return 'bg-red-100 text-red-800';
      case 'Limited': return 'bg-amber-100 text-amber-800';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-500';
    }
  };

  const updateMetric = (category, field, value) => {
    setNewReport(prev => ({
      ...prev,
      metrics: {
        ...prev.metrics,
        [category]: { ...prev.metrics[category], [field]: value }
      }
    }));
  };

  const handleConvertToPDP = async (suggestion) => {
    try {
      // Get active PDP cycle
      const cyclesRes = await axios.get(`${API}/pdp/cycles/athlete/${selectedAthlete}`);
      const activeCycle = cyclesRes.data.active_cycle;
      if (!activeCycle) {
        toast.error('No active PDP cycle found. Create a PDP cycle first.');
        return;
      }
      await axios.post(
        `${API}/match-reports-v2/pdp-integration/convert-to-goal?athlete_id=${selectedAthlete}&cycle_id=${activeCycle.id}`,
        suggestion
      );
      toast.success(`PDP Goal created: ${suggestion.title}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert to PDP goal');
    }
  };

  const summary = reportsData?.summary;
  const allReports = reportsData?.reports || [];
  // UX guard: when an Admin/Staff user lands on Test Player (contract fixture),
  // cap the visible list to the 3 most recent reports so the demo doesn't feel noisy.
  // Player/Parent roles always see the full dataset.
  const reports = useMemo(() => {
    if (!shouldLimitForContractFixture(selectedAthlete)) return allReports;
    return [...allReports]
      .sort((a, b) => new Date(b.match_date || 0).getTime() - new Date(a.match_date || 0).getTime())
      .slice(0, 3);
  }, [allReports, selectedAthlete]);
  const access = reportsData?.access;

  // Pre-compute a last-5-rating trend for each report (oldest → newest, ending
  // at that match). Reports list is already scoped to the selected athlete.
  const trendByReportId = useMemo(() => {
    const byDateAsc = [...reports]
      .filter((r) => r && r.match_date)
      .sort((a, b) => new Date(a.match_date) - new Date(b.match_date));
    const rolling = [];
    const map = {};
    byDateAsc.forEach((r) => {
      const rating = r.coach_evaluation?.overall_rating;
      if (rating != null) rolling.push(rating);
      map[r.id] = rolling.slice(-5);
    });
    return map;
  }, [reports]);

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="match-reports-page">
        <AthleteContextHeader athleteId={propAthleteId} moduleName="Match Reports" />
        <div className="mb-6 flex items-center gap-3">
          <img src="/pfap-logo-icon.png" alt="PFAP" className="h-10 w-10 hidden sm:block" />
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Match Reports
            </h2>
            <p className="text-gray-600 dark:text-gray-500 mt-1 text-sm">Track match performance with detailed metrics and evaluations</p>
          </div>
        </div>

        {/* Athlete Selector — hidden for Player/Parent (auto-selected to linked athlete) */}
        {!isLinkedViewer && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex gap-4 items-end">
                <div className="flex-1 max-w-md">
                  <Label>Select Athlete</Label>
                  <SearchableAthleteSelect
                    athletes={athletes}
                    value={selectedAthlete}
                    onChange={setSelectedAthlete}
                    placeholder="Choose an athlete..."
                    disabled={isScopedMode}
                    data-testid="athlete-select"
                  />
                </div>
                {canEdit && selectedAthlete && (
                  <Button
                    onClick={checkInjuryBeforeCreate}
                    className="bg-pfap-green hover:bg-pfap-green/90"
                    data-testid="create-report-btn"
                  >
                    + New Report
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Summary */}
        {selectedAthlete && summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-3xl font-bold text-emerald-600" data-testid="total-matches">{summary.total_matches}</p>
                <p className="text-sm text-gray-500 dark:text-gray-500">Total Matches</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-3xl font-bold text-blue-600" data-testid="avg-rating">
                  {summary.average_rating ? summary.average_rating.toFixed(1) : '-'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500">Avg. Rating</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-3xl font-bold text-purple-600" data-testid="total-minutes">{summary.total_minutes || 0}</p>
                <p className="text-sm text-gray-500 dark:text-gray-500">Total Minutes</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Performance Trends */}
        {selectedAthlete && trendData.length >= 2 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <Card data-testid="rating-trend-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-500">Rating Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <SafeChart height={200}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{fontSize: 11}} />
                    <YAxis domain={[0, 10]} tick={{fontSize: 11}} />
                    <Tooltip />
                    <Line type="monotone" dataKey="rating" stroke="#059669" strokeWidth={2} dot={{r: 4}} name="Rating" connectNulls />
                  </LineChart>
                </SafeChart>
              </CardContent>
            </Card>
            <Card data-testid="metrics-trend-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-500">Key Metrics Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <SafeChart height={200}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{fontSize: 11}} />
                    <YAxis domain={[0, 100]} tick={{fontSize: 11}} />
                    <Tooltip />
                    <Legend wrapperStyle={{fontSize: 11}} />
                    <Line type="monotone" dataKey="pass_accuracy" stroke="#3b82f6" strokeWidth={2} dot={{r: 3}} name="Pass Accuracy %" connectNulls />
                    <Line type="monotone" dataKey="stamina" stroke="#ef4444" strokeWidth={2} dot={{r: 3}} name="Stamina" connectNulls />
                    <Line type="monotone" dataKey="decision_making" stroke="#8b5cf6" strokeWidth={2} dot={{r: 3}} name="Decision Making" connectNulls />
                  </LineChart>
                </SafeChart>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Reports List */}
        {selectedAthlete ? (
          <Card>
            <CardHeader>
              <CardTitle>Match History</CardTitle>
              <CardDescription>{reports.length} reports on file</CardDescription>
            </CardHeader>
            <CardContent>
              {reports.length === 0 ? (
                <div className="py-12 text-center text-gray-500 dark:text-gray-500" data-testid="no-reports">
                  No match reports found.{canEdit && ' Click "+ New Report" to create one.'}
                </div>
              ) : (
                <div className="space-y-3">
                  {reports.map(report => (
                    <div
                      key={report.id}
                      className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:border-gray-700 transition-colors cursor-pointer"
                      onClick={() => openReportDetail(report.id)}
                      data-testid={`report-row-${report.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-900 dark:text-gray-100">vs {report.opponent}</span>
                              {report.coach_evaluation && (
                                <Badge className={getRatingColor(report.coach_evaluation.overall_rating)}>
                                  {report.coach_evaluation.overall_rating}/10
                                </Badge>
                              )}
                              {report.coach_evaluation && trendByReportId[report.id]?.length > 1 && (
                                <span
                                  className="inline-flex items-center"
                                  data-testid={`rating-sparkline-row-${report.id}`}
                                  aria-label="Last 5 match ratings trend"
                                >
                                  <RatingSparkline values={trendByReportId[report.id]} />
                                </span>
                              )}
                              <Badge className={getAvailabilityBadge(report.availability_status)}>
                                {report.availability_status}
                              </Badge>
                              {report.post_injury_indicator && (
                                <Badge className="bg-orange-100 text-orange-800">Post-Injury</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-500 mt-1">
                              <span>{new Date(report.match_date).toLocaleDateString()}</span>
                              {report.competition && <span>{report.competition}</span>}
                              {report.position && <span>{report.position}</span>}
                              {report.minutes_played && <span>{report.minutes_played} mins</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {canEdit && !report.coach_evaluation && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); openEvalDialog(report); }}
                              data-testid={`eval-btn-${report.id}`}
                            >
                              Evaluate
                            </Button>
                          )}
                          {isPlayer && !report.player_reflection && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); openReflectDialog(report); }}
                              data-testid={`reflect-btn-${report.id}`}
                            >
                              Reflect
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Show eval/reflection summary if present */}
                      {(report.coach_evaluation || report.player_reflection) && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-3">
                          {report.coach_evaluation && (
                            <div data-testid={`embedded-coach-eval-${report.id}`}>
                              <div className="flex items-center gap-2 flex-wrap mb-2">
                                <span className="text-[11px] font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-400">
                                  Coach Evaluation
                                </span>
                                <Badge
                                  className={getRatingColor(report.coach_evaluation.overall_rating)}
                                  data-testid={`embedded-eval-rating-${report.id}`}
                                >
                                  {report.coach_evaluation.overall_rating}/10
                                </Badge>
                                {trendByReportId[report.id]?.length > 0 && (
                                  <span
                                    className="inline-flex items-center"
                                    data-testid={`rating-sparkline-${report.id}`}
                                    aria-label="Last 5 match ratings"
                                  >
                                    <RatingSparkline values={trendByReportId[report.id]} />
                                  </span>
                                )}
                                {report.coach_evaluation.coach_name && (
                                  <span className="text-[11px] text-gray-400 dark:text-gray-500">
                                    By {report.coach_evaluation.coach_name}
                                  </span>
                                )}
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {report.coach_evaluation.strengths && (
                                  <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-900/40 rounded-md p-2.5">
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mb-1">
                                      Strengths
                                    </p>
                                    <p className="text-xs text-gray-700 dark:text-gray-300 leading-snug whitespace-pre-line">
                                      {report.coach_evaluation.strengths}
                                    </p>
                                  </div>
                                )}
                                {report.coach_evaluation.areas_to_improve && (
                                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900/40 rounded-md p-2.5">
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-1">
                                      Areas to Improve
                                    </p>
                                    <p className="text-xs text-gray-700 dark:text-gray-300 leading-snug whitespace-pre-line">
                                      {report.coach_evaluation.areas_to_improve}
                                    </p>
                                  </div>
                                )}
                              </div>
                              {report.coach_evaluation.tactical_notes && (
                                <details
                                  className="mt-2"
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`tactical-notes-toggle-${report.id}`}
                                >
                                  <summary className="cursor-pointer text-[11px] font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 select-none inline-flex items-center gap-1">
                                    Tactical Notes
                                    <span className="text-[10px] opacity-70">▾</span>
                                  </summary>
                                  <div className="mt-1.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/40 rounded-md p-2.5">
                                    <p className="text-xs text-gray-700 dark:text-gray-300 leading-snug whitespace-pre-line">
                                      {report.coach_evaluation.tactical_notes}
                                    </p>
                                  </div>
                                </details>
                              )}
                            </div>
                          )}
                          {report.player_reflection && (
                            <div>
                              <p className="text-[11px] font-semibold uppercase tracking-wider text-purple-700 dark:text-purple-400 mb-1">
                                Player Reflection · {report.player_reflection.self_rating}/10
                              </p>
                              {report.player_reflection.what_went_well && (
                                <p className="text-xs text-gray-600 dark:text-gray-400 leading-snug">
                                  <span className="font-medium">Good:</span> {report.player_reflection.what_went_well}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-12 text-center text-gray-500 dark:text-gray-500">
              {isLinkedViewer
                ? (isParent ? 'No match reports for your child yet — they will appear here after coaches record matches.' : 'No match reports yet — your coach will add them after matches.')
                : 'Select an athlete to view their match reports'}
            </CardContent>
          </Card>
        )}

        {/* ===== CREATE REPORT DIALOG ===== */}
        <Dialog open={createDialog} onOpenChange={(open) => { if (!open) resetReportForm(); setCreateDialog(open); }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Match Report</DialogTitle>
              <DialogDescription>Record match performance with detailed metrics</DialogDescription>
            </DialogHeader>

            {/* Injury Warning */}
            {injuryStatus?.has_active_injury && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800" data-testid="injury-warning">
                This athlete has {injuryStatus.active_count} active injury/injuries. Availability has been set to "Injured". Full match reporting as "Fit" is blocked.
              </div>
            )}

            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Match Date *</Label>
                  <Input type="date" value={newReport.match_date} onChange={e => setNewReport(p => ({ ...p, match_date: e.target.value }))} data-testid="report-date-input" />
                </div>
                <div>
                  <Label>Opponent *</Label>
                  <Input value={newReport.opponent} onChange={e => setNewReport(p => ({ ...p, opponent: e.target.value }))} placeholder="e.g., Benfica U17" data-testid="report-opponent-input" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Competition</Label>
                  <Select value={newReport.competition} onValueChange={v => setNewReport(p => ({ ...p, competition: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(config?.competitions || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Position</Label>
                  <Select value={newReport.position} onValueChange={v => setNewReport(p => ({ ...p, position: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {(config?.positions || []).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Minutes Played</Label>
                  <Input type="number" min="0" max="120" value={newReport.minutes_played} onChange={e => setNewReport(p => ({ ...p, minutes_played: e.target.value }))} placeholder="90" />
                </div>
              </div>
              <div>
                <Label>Availability Status</Label>
                <Select value={newReport.availability_status} onValueChange={v => setNewReport(p => ({ ...p, availability_status: v }))} disabled={injuryStatus?.has_active_injury}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(config?.availability_statuses || ['Fit', 'Injured', 'Limited']).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Performance Metrics */}
              <div className="border-t pt-4">
                <p className="font-medium mb-3">Performance Metrics</p>
                <Tabs defaultValue="technical">
                  <TabsList className="grid grid-cols-4 w-full">
                    <TabsTrigger value="technical">Technical</TabsTrigger>
                    <TabsTrigger value="tactical">Tactical</TabsTrigger>
                    <TabsTrigger value="physical">Physical</TabsTrigger>
                    <TabsTrigger value="mental">Mental</TabsTrigger>
                  </TabsList>
                  <TabsContent value="technical" className="space-y-3 mt-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Pass Accuracy (0-100%)</Label>
                        <Input type="number" min="0" max="100" value={newReport.metrics.technical.pass_accuracy} onChange={e => updateMetric('technical', 'pass_accuracy', e.target.value)} placeholder="%" />
                      </div>
                      <div>
                        <Label className="text-xs">First Touch (1-10)</Label>
                        <Input type="number" min="1" max="10" value={newReport.metrics.technical.first_touch} onChange={e => updateMetric('technical', 'first_touch', e.target.value)} placeholder="1-10" />
                      </div>
                      <div>
                        <Label className="text-xs">Dribbles Success</Label>
                        <Input type="number" min="0" value={newReport.metrics.technical.dribbles_success} onChange={e => updateMetric('technical', 'dribbles_success', e.target.value)} placeholder="#" />
                      </div>
                      <div>
                        <Label className="text-xs">Dribbles Attempts</Label>
                        <Input type="number" min="0" value={newReport.metrics.technical.dribbles_attempts} onChange={e => updateMetric('technical', 'dribbles_attempts', e.target.value)} placeholder="#" />
                      </div>
                    </div>
                  </TabsContent>
                  <TabsContent value="tactical" className="space-y-3 mt-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Positioning (1-10)</Label>
                        <Input type="number" min="1" max="10" value={newReport.metrics.tactical.positioning} onChange={e => updateMetric('tactical', 'positioning', e.target.value)} placeholder="1-10" />
                      </div>
                      <div>
                        <Label className="text-xs">Decision Making (1-10)</Label>
                        <Input type="number" min="1" max="10" value={newReport.metrics.tactical.decision_making} onChange={e => updateMetric('tactical', 'decision_making', e.target.value)} placeholder="1-10" />
                      </div>
                    </div>
                  </TabsContent>
                  <TabsContent value="physical" className="space-y-3 mt-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Distance (km)</Label>
                        <Input type="number" step="0.1" min="0" value={newReport.metrics.physical.distance_covered_km} onChange={e => updateMetric('physical', 'distance_covered_km', e.target.value)} placeholder="km" />
                      </div>
                      <div>
                        <Label className="text-xs">Sprints</Label>
                        <Input type="number" min="0" value={newReport.metrics.physical.sprint_count} onChange={e => updateMetric('physical', 'sprint_count', e.target.value)} placeholder="#" />
                      </div>
                      <div>
                        <Label className="text-xs">Stamina (1-10)</Label>
                        <Input type="number" min="1" max="10" value={newReport.metrics.physical.stamina} onChange={e => updateMetric('physical', 'stamina', e.target.value)} placeholder="1-10" />
                      </div>
                    </div>
                  </TabsContent>
                  <TabsContent value="mental" className="space-y-3 mt-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Focus (1-10)</Label>
                        <Input type="number" min="1" max="10" value={newReport.metrics.mental.focus} onChange={e => updateMetric('mental', 'focus', e.target.value)} placeholder="1-10" />
                      </div>
                      <div>
                        <Label className="text-xs">Confidence (1-10)</Label>
                        <Input type="number" min="1" max="10" value={newReport.metrics.mental.confidence} onChange={e => updateMetric('mental', 'confidence', e.target.value)} placeholder="1-10" />
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setCreateDialog(false); resetReportForm(); }}>Cancel</Button>
              <Button onClick={handleCreateReport} className="bg-pfap-green hover:bg-pfap-green/90" data-testid="save-report-btn">
                Create Report
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== REPORT DETAIL DIALOG ===== */}
        <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Match Report Detail</DialogTitle>
            </DialogHeader>
            {selectedReport && (
              <div className="space-y-4" data-testid="report-detail">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-lg font-bold">vs {selectedReport.report?.opponent}</span>
                  <Badge className={getAvailabilityBadge(selectedReport.report?.availability_status)}>
                    {selectedReport.report?.availability_status}
                  </Badge>
                  {selectedReport.report?.post_injury_indicator && (
                    <Badge className="bg-orange-100 text-orange-800">Post-Injury</Badge>
                  )}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-500 flex gap-3 flex-wrap">
                  <span>{selectedReport.report?.match_date}</span>
                  <span>{selectedReport.report?.competition}</span>
                  <span>{selectedReport.report?.position}</span>
                  <span>{selectedReport.report?.minutes_played} mins</span>
                </div>

                {/* Metrics Display */}
                {selectedReport.report?.metrics && (
                  <div className="border-t pt-3">
                    <p className="font-medium mb-2">Performance Metrics</p>
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(selectedReport.report.metrics).map(([cat, values]) => (
                        values && (
                          <div key={cat} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                            <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-500 mb-2">{cat}</p>
                            {Object.entries(values).map(([key, val]) => (
                              val !== null && val !== undefined && (
                                <div key={key} className="flex justify-between text-sm">
                                  <span className="text-gray-600 dark:text-gray-500">{key.replace(/_/g, ' ')}</span>
                                  <span className="font-medium">{val}{key === 'pass_accuracy' ? '%' : key === 'distance_covered_km' ? ' km' : ''}</span>
                                </div>
                              )
                            ))}
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}

                {/* Coach Evaluation */}
                {selectedReport.coach_evaluation && (
                  <div className="border-t pt-3">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="font-medium">Coach Evaluation</p>
                      <Badge className={getRatingColor(selectedReport.coach_evaluation.overall_rating)}>
                        {selectedReport.coach_evaluation.overall_rating}/10
                      </Badge>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 space-y-1 text-sm">
                      {selectedReport.coach_evaluation.strengths && <p><span className="font-medium">Strengths:</span> {selectedReport.coach_evaluation.strengths}</p>}
                      {selectedReport.coach_evaluation.areas_to_improve && <p><span className="font-medium">Areas to Improve:</span> {selectedReport.coach_evaluation.areas_to_improve}</p>}
                      {selectedReport.coach_evaluation.tactical_notes && <p><span className="font-medium">Tactical Notes:</span> {selectedReport.coach_evaluation.tactical_notes}</p>}
                      <p className="text-xs text-gray-500 dark:text-gray-500">By {selectedReport.coach_evaluation.coach_name}</p>
                    </div>
                  </div>
                )}

                {/* Player Reflection */}
                {selectedReport.player_reflection && (
                  <div className="border-t pt-3">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="font-medium">Player Reflection</p>
                      <Badge className={getRatingColor(selectedReport.player_reflection.self_rating)}>
                        Self: {selectedReport.player_reflection.self_rating}/10
                      </Badge>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3 space-y-1 text-sm">
                      {selectedReport.player_reflection.what_went_well && <p><span className="font-medium">What went well:</span> {selectedReport.player_reflection.what_went_well}</p>}
                      {selectedReport.player_reflection.what_to_improve && <p><span className="font-medium">To improve:</span> {selectedReport.player_reflection.what_to_improve}</p>}
                    </div>
                  </div>
                )}

                {/* PDP Suggestions */}
                {selectedReport.pdp_suggestions?.length > 0 && (
                  <div className="border-t pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium">PDP Suggestions</p>
                      <span className="text-xs text-gray-500 dark:text-gray-500">Areas below threshold (rating &lt; 6)</span>
                    </div>
                    <div className="space-y-2">
                      {selectedReport.pdp_suggestions.map((s, i) => (
                        <div key={i} className="bg-amber-50 rounded-lg p-3 flex items-center justify-between">
                          <div className="flex-1">
                            <Badge variant="outline" className="mr-2">{s.category}</Badge>
                            <span className="text-sm">{s.title}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={s.priority === 'high' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}>
                              {s.priority}
                            </Badge>
                            {canEdit && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                onClick={() => handleConvertToPDP(s)}
                                data-testid={`convert-pdp-btn-${i}`}
                              >
                                Convert to PDP Goal
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ===== COACH EVALUATION DIALOG ===== */}
        <Dialog open={evalDialog} onOpenChange={setEvalDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Coach Evaluation</DialogTitle>
              <DialogDescription>Rate the player performance for this match</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Overall Rating (1-10) *</Label>
                <Input type="number" min="1" max="10" value={newEval.overall_rating} onChange={e => setNewEval(p => ({ ...p, overall_rating: e.target.value }))} data-testid="eval-rating-input" />
              </div>
              <div>
                <Label>Strengths</Label>
                <Textarea value={newEval.strengths} onChange={e => setNewEval(p => ({ ...p, strengths: e.target.value }))} placeholder="What did the player do well?" />
              </div>
              <div>
                <Label>Areas to Improve</Label>
                <Textarea value={newEval.areas_to_improve} onChange={e => setNewEval(p => ({ ...p, areas_to_improve: e.target.value }))} placeholder="What needs work?" data-testid="eval-areas-input" />
              </div>
              <div>
                <Label>Tactical Notes</Label>
                <Textarea value={newEval.tactical_notes} onChange={e => setNewEval(p => ({ ...p, tactical_notes: e.target.value }))} placeholder="Tactical observations..." />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEvalDialog(false)}>Cancel</Button>
              <Button onClick={handleSubmitEvaluation} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-eval-btn">Submit Evaluation</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== PLAYER REFLECTION DIALOG ===== */}
        <Dialog open={reflectDialog} onOpenChange={setReflectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Self Reflection</DialogTitle>
              <DialogDescription>Reflect on your match performance</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Self Rating (1-10) *</Label>
                <Input type="number" min="1" max="10" value={newReflection.self_rating} onChange={e => setNewReflection(p => ({ ...p, self_rating: e.target.value }))} data-testid="reflect-rating-input" />
              </div>
              <div>
                <Label>What went well?</Label>
                <Textarea value={newReflection.what_went_well} onChange={e => setNewReflection(p => ({ ...p, what_went_well: e.target.value }))} placeholder="Positive aspects of your performance..." />
              </div>
              <div>
                <Label>What to improve?</Label>
                <Textarea value={newReflection.what_to_improve} onChange={e => setNewReflection(p => ({ ...p, what_to_improve: e.target.value }))} placeholder="Areas for development..." />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReflectDialog(false)}>Cancel</Button>
              <Button onClick={handleSubmitReflection} className="bg-purple-600 hover:bg-purple-700" data-testid="submit-reflect-btn">Submit Reflection</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default MatchReportsPage;
